import React from 'react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  TooltipProps,
} from 'recharts';
import { Activity, Loader2, TrendingUp } from 'lucide-react';

/**
 * Interface for health history data
 */
export interface HealthHistoryData {
  date: string;
  treatments: number;
  vaccinations: number;
  checkups: number;
  healthyCount?: number;
  sickCount?: number;
}

interface HealthHistoryChartProps {
  data: HealthHistoryData[];
  loading?: boolean;
  error?: string | null;
  height?: number;
}

/**
 * Custom tooltip for health history
 */
const CustomTooltip: React.FC<TooltipProps<number, string>> = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
        <p className="font-semibold text-gray-700 mb-2">{label}</p>
        <div className="space-y-1">
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              <span className="font-semibold">{entry.name}:</span> {entry.value}
            </p>
          ))}
        </div>
      </div>
    );
  }
  return null;
};

/**
 * HealthHistoryChart - Display health metrics over time
 */
const HealthHistoryChart: React.FC<HealthHistoryChartProps> = ({
  data,
  loading = false,
  error = null,
  height = 350,
}) => {
  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <Loader2 className="w-8 h-8 animate-spin text-red-600" />
        <span className="ml-2 text-gray-600">Loading health data...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <div className="text-center">
          <p className="text-red-600 font-semibold">Failed to load chart</p>
          <p className="text-sm text-gray-500 mt-1">{error}</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <div className="text-center text-gray-500">
          <Activity className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No health history data available</p>
        </div>
      </div>
    );
  }

  // Calculate statistics
  const totalTreatments = data.reduce((sum, item) => sum + (item.treatments || 0), 0);
  const totalVaccinations = data.reduce((sum, item) => sum + (item.vaccinations || 0), 0);
  const totalCheckups = data.reduce((sum, item) => sum + (item.checkups || 0), 0);
  const totalHealthEvents = totalTreatments + totalVaccinations + totalCheckups;

  return (
    <div className="w-full">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center">
          <Activity className="w-5 h-5 mr-2 text-red-600" />
          Health History & Trends
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Medical activities and health records over time
        </p>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        <div className="bg-red-50 p-3 rounded-lg">
          <p className="text-xs text-gray-600">Total Treatments</p>
          <p className="text-lg font-bold text-red-600">{totalTreatments}</p>
        </div>
        <div className="bg-blue-50 p-3 rounded-lg">
          <p className="text-xs text-gray-600">Total Vaccinations</p>
          <p className="text-lg font-bold text-blue-600">{totalVaccinations}</p>
        </div>
        <div className="bg-green-50 p-3 rounded-lg">
          <p className="text-xs text-gray-600">Total Checkups</p>
          <p className="text-lg font-bold text-green-600">{totalCheckups}</p>
        </div>
        <div className="bg-purple-50 p-3 rounded-lg">
          <p className="text-xs text-gray-600">Health Events</p>
          <p className="text-lg font-bold text-purple-600">{totalHealthEvents}</p>
        </div>
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={height - 160}>
        <AreaChart
          data={data}
          margin={{ top: 20, right: 30, left: 0, bottom: 60 }}
        >
          <defs>
            <linearGradient id="colorTreatments" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#ef4444" stopOpacity={0.8} />
              <stop offset="95%" stopColor="#ef4444" stopOpacity={0.1} />
            </linearGradient>
            <linearGradient id="colorVaccinations" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8} />
              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1} />
            </linearGradient>
            <linearGradient id="colorCheckups" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#10b981" stopOpacity={0.8} />
              <stop offset="95%" stopColor="#10b981" stopOpacity={0.1} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" angle={-45} textAnchor="end" height={80} />
          <YAxis label={{ value: 'Count', angle: -90, position: 'insideLeft' }} />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          <Area
            type="monotone"
            dataKey="treatments"
            stroke="#ef4444"
            fillOpacity={1}
            fill="url(#colorTreatments)"
            name="Treatments"
          />
          <Area
            type="monotone"
            dataKey="vaccinations"
            stroke="#3b82f6"
            fillOpacity={1}
            fill="url(#colorVaccinations)"
            name="Vaccinations"
          />
          <Area
            type="monotone"
            dataKey="checkups"
            stroke="#10b981"
            fillOpacity={1}
            fill="url(#colorCheckups)"
            name="Checkups"
          />
        </AreaChart>
      </ResponsiveContainer>

      {/* Additional Info */}
      <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
        <p className="text-sm text-blue-800 flex items-center gap-2">
          <TrendingUp className="w-4 h-4" />
          <span>Health activities are tracked daily. Regular vaccinations and checkups help maintain herd wellness.</span>
        </p>
      </div>
    </div>
  );
};

export default HealthHistoryChart;
